'use client';

import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import type { TFunction } from 'i18next';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { AuditEvent, AuditEventOptions } from '@/lib/api/audit';

import { getActionLabelKey, humanizeAction } from '../utils/audit-labels';

interface GetAuditTrailTableColumnsProps {
  t: TFunction;
  options: AuditEventOptions;
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export function getAuditTrailTableColumns({
  t,
  options,
}: GetAuditTrailTableColumnsProps): ColumnDef<AuditEvent>[] {
  return [
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('auditTrail.columns.date')} />,
      cell: ({ row }) => {
        const date = row.original.createdAt;
        return (
          <div className="flex flex-col">
            <span className="text-sm tabular-nums">{format(date, 'MMM dd, HH:mm')}</span>
            <span className="text-muted-foreground text-xs">{format(date, 'yyyy')}</span>
          </div>
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('auditTrail.columns.date'),
        variant: 'dateRange',
      },
      size: 130,
    },
    {
      id: 'action',
      accessorKey: 'action',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('auditTrail.columns.action')} />,
      cell: ({ row }) => {
        const action = row.original.action;
        return (
          <span className="text-sm font-medium">
            {t(getActionLabelKey(action), { defaultValue: humanizeAction(action) })}
          </span>
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('auditTrail.columns.action'),
        variant: 'multiSelect',
        options: options.action.map((action) => ({
          label: t(getActionLabelKey(action), { defaultValue: humanizeAction(action) }),
          value: action,
        })),
      },
      size: 220,
    },
    {
      id: 'operation',
      accessorKey: 'operation',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('auditTrail.columns.operation')} />
      ),
      cell: ({ row }) => <span className="text-sm">{capitalize(row.original.operation)}</span>,
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: t('auditTrail.columns.operation'),
        variant: 'multiSelect',
        options: options.operation.map((operation) => ({
          label: capitalize(operation),
          value: operation,
        })),
      },
      size: 110,
    },
    {
      id: 'entityType',
      accessorKey: 'entityType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('auditTrail.columns.entity')} />,
      cell: ({ row }) => {
        const { entityType, entityLabel } = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{entityLabel ?? entityType}</div>
            {entityLabel && <div className="text-muted-foreground truncate text-xs">{entityType}</div>}
          </div>
        );
      },
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: t('auditTrail.columns.entity'),
        variant: 'multiSelect',
        options: options.entityType.map((entityType) => ({
          label: entityType,
          value: entityType,
        })),
      },
      size: 180,
    },
    {
      id: 'actorId',
      accessorKey: 'actorId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('auditTrail.columns.actor')} />,
      cell: ({ row }) => {
        const { actorLabel, actorEmail, actorId } = row.original;
        const primary = actorLabel ?? actorEmail ?? actorId;
        return (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{primary}</div>
            {actorLabel && actorEmail && (
              <div className="text-muted-foreground truncate text-xs">{actorEmail}</div>
            )}
          </div>
        );
      },
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: t('auditTrail.columns.actor'),
        variant: 'multiSelect',
        options: options.actor.map((actor) => ({
          label: actor.label,
          value: actor.id,
        })),
      },
      size: 200,
    },
    {
      id: 'storeId',
      accessorKey: 'storeId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('auditTrail.columns.store')} />,
      cell: ({ row }) => {
        const storeId = row.original.storeId;
        if (!storeId) {
          return <span className="text-muted-foreground text-xs">—</span>;
        }
        // The filter options already carry the names, so show one rather than a
        // raw id. Falls back to the id for a store deleted since the row was
        // written — audit rows deliberately outlive their entity.
        const name = options.storeId.find((store) => store.id === storeId)?.name;
        return name ? (
          <span className="truncate text-sm">{name}</span>
        ) : (
          <span className="truncate font-mono text-xs">{storeId}</span>
        );
      },
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        label: t('auditTrail.columns.store'),
        variant: 'multiSelect',
        options: options.storeId.map((store) => ({
          label: store.name,
          value: store.id,
        })),
      },
      size: 160,
    },
  ];
}
